import { useEffect, useMemo, useState } from 'react';
import { categories, type ResourceItem } from '@/mocks/resources';
import Header from './components/Header';
import Hero from './components/Hero';
import Sidebar from './components/Sidebar';
import CategorySection from './components/CategorySection';
import About from './components/About';
import Faq from './components/Faq';
import Footer from './components/Footer';

const allResources: ResourceItem[] = categories.flatMap((c) => c.list);

export default function Home() {
  const [query, setQuery] = useState('');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string>('efficiency');
  const [showTop, setShowTop] = useState(false);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  const visible = useMemo(() => {
    if (!searching) return categories;
    return categories
      .map((c) => ({
        ...c,
        list: c.list.filter((i) =>
          [i.name, i.desc, i.tag, i.extra].join(' ').toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.list.length > 0);
  }, [searching, q]);

  const matchCount = visible.reduce((sum, c) => sum + c.list.length, 0);

  const savedItems = useMemo(
    () => allResources.filter((it) => savedIds.has(it.id)),
    [savedIds]
  );

  const toggleSaved = (id: string) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 返回顶部按钮显示
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // 分类高亮跟随滚动（仅在未搜索时）
  useEffect(() => {
    if (searching) return;
    const ids = categories.map((c) => c.id);
    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (hit && hit.target.id) setActiveId(hit.target.id);
      },
      { rootMargin: '-120px 0px -65% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [searching]);

  const handleJump = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTag = (tag: string) => {
    setQuery(tag);
    document.getElementById('directory')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Header
        categories={categories}
        savedItems={savedItems}
        onRemoveSaved={(id) => toggleSaved(id)}
        onJump={handleJump}
      />

      <main>
        <Hero query={query} onQuery={setQuery} categories={categories} onTag={handleTag} />

        {/* 资源分类目录 */}
        <section id="directory" aria-label="资源分类目录" className="bg-background-100 py-10 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-accent-700">
                  <i className="ri-stack-line text-sm" aria-hidden="true" />
                  资源目录
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {searching ? `“${query.trim()}”的搜索结果` : '按分类查资源'}
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  {searching
                    ? `共找到 ${matchCount} 项匹配资源`
                    : '点左侧分类可快速跳转，鼠标悬停卡片即可收藏'}
                </p>
              </div>
              {searching && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-refresh-line text-base" aria-hidden="true" />
                  清除搜索
                </button>
              )}
            </div>

            <div className="flex items-start gap-8">
              <Sidebar
                categories={categories}
                activeId={activeId}
                savedCount={savedIds.size}
                onJump={handleJump}
              />

              <div className="min-w-0 flex-1">
                {visible.length === 0 ? (
                  <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 py-20 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                      <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
                    </span>
                    <p className="mt-4 text-sm font-semibold text-foreground-900">没有找到匹配的资源</p>
                    <p className="mt-1 text-xs text-foreground-600">换个关键词试试，或清空搜索浏览全部分类</p>
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      className="mt-5 whitespace-nowrap rounded-md border border-primary-300 px-4 py-2 text-sm text-primary-700 hover:bg-primary-50"
                    >
                      浏览全部资源
                    </button>
                  </div>
                ) : (
                  <div className="space-y-12 md:space-y-14">
                    {visible.map((cat, idx) => (
                      <CategorySection
                        key={cat.id}
                        category={cat}
                        index={idx}
                        savedIds={savedIds}
                        onToggle={toggleSaved}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="bg-background-100 pb-10 md:pb-14" />
        <About />
        <div className="h-10 bg-background-100 md:h-14" />
        <Faq />
      </main>

      <div className="bg-background-100 pt-10 md:pt-14">
        <Footer categories={categories} />
      </div>

      {/* 回到顶部 */}
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-none transition-transform hover:scale-105 hover:bg-primary-600"
          aria-label="回到顶部"
        >
          <i className="ri-arrow-up-line text-xl" aria-hidden="true" />
        </button>
      )}
    </>
  );
}